import React, { useEffect, useRef, useState } from 'react';
import { ccmApi } from '../lib/api';
import { nextPollDelay, POLL_BUDGET_MS } from '../lib/note-expander-v2';
import { outboxLine, outboxSettled, undoable } from '../lib/note-expander-v2-review';

// Note Expander v2, "Written to the chart" (PR 5-UI-2, O191; mockup "done"; DESIGN §6.2). Every
// write goes through the outbox with its own idempotency key, and an uncertain one is read back
// before any retry, so this list follows each row (GET /v2/revisions/:id/outbox) until it is
// written or failed. A note's vitals and chart items are queued once the note is confirmed, so
// the list is final only after it has held still for two polls. Undo removes only the chart items
// this revision created (the server's ledger claim), never anything else.

const ELATION_BASE = 'https://app.elationemr.com/patient/';

function seed(rows) {
  return (rows || []).map((r) => ({
    id: r.id, kind: r.kind, status: r.status || 'pending',
    elationId: r.elationId != null ? String(r.elationId) : null, lastErrorCode: r.lastErrorCode || null,
  }));
}

export function NoteExpanderV2Done({ revisionId, initialOutbox, itemLabels, copy, patientElationId, onBack }) {
  const [rows, setRows] = useState(() => seed(initialOutbox));
  const [stale, setStale] = useState(false);
  const [settled, setSettled] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoResults, setUndoResults] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const since = Date.now();
    let attempt = 0;
    let still = 0;
    let lastCount = -1;
    let timer = null;
    const tick = async () => {
      if (!mounted.current) return;
      if (Date.now() - since > POLL_BUDGET_MS) { setStale(true); return; }
      try {
        const r = await ccmApi.ne2Outbox(revisionId);
        if (!mounted.current) return;
        const next = seed(r.outbox);
        setRows(next);
        if (outboxSettled(next) && next.length === lastCount) still += 1; else still = 0;
        lastCount = next.length;
        if (still >= 2) { setSettled(true); return; }
      } catch (_) { /* retried on the next tick */ }
      attempt += 1;
      timer = setTimeout(tick, nextPollDelay(attempt));
    };
    timer = setTimeout(tick, nextPollDelay(0));
    return () => { mounted.current = false; clearTimeout(timer); };
  }, [revisionId]);

  async function undo() {
    setUndoing(true);
    try {
      const r = await ccmApi.ne2Undo(revisionId, undoable(rows));
      setUndoResults(r.results || []);
    } catch (_) {
      setUndoResults([{ ok: false, error: 'request failed' }]);
    } finally {
      setUndoing(false);
    }
  }

  // Lines: the note, the vitals as one line with a count, each chart item named.
  const seen = { problem: 0, immunization: 0, medication: 0 };
  const vitals = rows.filter((r) => r.kind === 'vitals');
  const lines = [];
  for (const r of rows) {
    if (r.kind === 'vitals') continue;
    const line = outboxLine(r, copy);
    let label = line.label;
    if (seen[r.kind] != null) {
      const name = itemLabels && itemLabels[r.kind] ? itemLabels[r.kind][seen[r.kind]] : null;
      seen[r.kind] += 1;
      if (name) label = `${label}: ${name}`;
    }
    lines.push({ key: r.id || `${r.kind}-${lines.length}`, ...line, label });
  }
  if (vitals.length) {
    const failed = vitals.filter((r) => r.status === 'failed');
    const writing = vitals.some((r) => r.status !== 'confirmed' && r.status !== 'failed');
    const first = failed[0] ? outboxLine(failed[0], copy) : null;
    lines.push({
      key: 'vitals',
      label: `Vitals carried onto the copy: ${vitals.length} record${vitals.length === 1 ? '' : 's'}`,
      words: failed.length ? first.words : writing ? 'Writing…' : 'Written',
      tone: failed.length ? 'failed' : writing ? 'running' : 'done',
    });
  }
  const canUndo = undoable(rows).length > 0 && !undoResults;

  return (
    <div className="ne2-done">
      <h3>Written to the chart</h3>
      {!rows.length && <div className="ne-empty">Nothing was queued for this expansion.</div>}
      <ul className="ne2-done-list">
        {lines.map((l) => (
          <li key={l.key} className={'ne2-done-row is-' + l.tone}>
            <span className="ne2-done-mark" aria-hidden="true">{l.tone === 'done' ? '✓' : l.tone === 'failed' ? '!' : '…'}</span>
            <span className="ne2-done-label">{l.label}</span>
            <span className="ne2-done-state">{l.words}</span>
          </li>
        ))}
      </ul>
      {stale && !settled && <div className="ne2-hint" role="status">Still checking the chart. Come back later; nothing will be written twice.</div>}
      <p className="ne2-done-next">Next in Elation: compare the copy with your stub, delete the stub, and sign the copy.</p>
      {undoResults && (
        <ul className="ne2-done-list">
          {undoResults.map((u, i) => (
            <li key={i} className={'ne2-done-row is-' + (u.ok ? 'done' : 'failed')}>
              <span className="ne2-done-label">{u.kind ? `${u.kind[0].toUpperCase()}${u.kind.slice(1)}` : 'Undo'}</span>
              <span className="ne2-done-state">{u.ok ? 'Removed from the chart' : 'Could not be undone'}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="ne2-review-actions">
        {patientElationId != null && (
          <a className="btn accent" href={ELATION_BASE + encodeURIComponent(patientElationId)} target="_blank" rel="noopener noreferrer">Open in Elation</a>
        )}
        {canUndo && <button type="button" className="btn" onClick={undo} disabled={undoing}>{undoing ? 'Undoing…' : 'Undo chart items'}</button>}
        <button type="button" className="btn ghost" onClick={onBack}>Back to inbox</button>
      </div>
      <p className="ne2-hint">Undo removes only items this run created, never anything else in the chart.</p>
    </div>
  );
}
